import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy} from '@angular/core';
import {groupBy, uniqBy} from 'lodash-es';
import {MapService} from '../../shared/map/map.service';
import {AutomationService} from '../automation.service';
import {AutomationMapping} from '../automation-mapping.model';
import {convertFeatureToMapObject} from '../../shared/utils';
import * as tagDefinitions from '../tag-definitions';
import {BaseComponent} from '../../components/general/base/base.component';
import {Observable, Subject} from 'rxjs';
import {debounceTime, map, takeUntil, distinctUntilChanged} from 'rxjs/operators';
import { ILayer } from '../../shared/map/layer/layer.type';
import { AuthService } from '~app/auth/auth.service';
import { MapObject } from '../../shared/map/types';

@Component({
  selector: 'qm-automation-admin',
  templateUrl: './automation-admin.component.html',
  styleUrls: ['./automation-admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutomationAdminComponent extends BaseComponent<AutomationMapping> implements OnInit, OnDestroy {
  public deviceList: string[];
  public editDevice: string;
  public currentMapping: MapObject = null;
  public mapObjectList: MapObject[] = [];
  public mappingList: AutomationMapping[] = [];
  public selectedLayer: string;
  public layers: ILayer[];
  public prefix = '';
  public postfix = '';
  public regoId: string;

  private ngUnsubscribe: Subject<boolean> = new Subject();

  public search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => {
        if (!term) {
          return [];
        }
        term = term.toLowerCase();
        return this.mapObjectList.filter(obj => obj.title && obj.title.toLowerCase().includes(term)).slice(0, 10);
      })
    )

  constructor(private automationService: AutomationService,
              private mapService: MapService,
              private authService: AuthService,
              private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.name = 'AutomationAdminComponent';
  }

  ngOnInit() {
    this.loadDevices();
    this.layers = this.mapService.getLayerList();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async loadDevices() {
    this.mappingList = [];
    const list = await this.automationService.list();
    if (list.isOk) {
      // Duplicator will automatically remove all duplicate mappings! Buahaha!
      this.duplicator(list.data);
      // if (list && list.length > 0) {
      for (const row of list.data) {
        this.mappingList[row.automationId] = row;
      }
    }
    // }

    const tenant = 'T' + this.authService.getTenant();
    this.deviceList = tagDefinitions[tenant]();

    this.mapService.visibleMarkers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(markers => {
      this.mapObjectList = Array.from(this.mapService.getLayerFeatures()).flatMap(([layerName, features]) => {
        return features.map(feature => convertFeatureToMapObject(feature));
      });
      this.mapObjectList = this.mapObjectList.concat(markers.map(marker => {
        const l = marker.layer.metadata;
        return {id: marker.properties[l.uniqueBy], title: marker.properties[l.labelBy], layerId: marker.layer.id} as MapObject;
      }));
      this.changeDetectorRef.markForCheck();
    });
  }

  private duplicator(list: AutomationMapping[]) {
    console.log(list);
    const group = groupBy(list, (o) => {
      return o.automationId;
    });
    Object.keys(group).forEach(key => {
      if (group[key].length > 1) {
        // console.log('Duplicates found for');
        console.log(key);
        console.log(group[key]);
        group[key].forEach(mapping => {
          this.automationService.remove(mapping);
        });
      }
    });
  }

  public setEditDevice(asset: string) {
    this.editDevice = asset;
    this.currentMapping = null;
  }

  public async saveDeviceMapping() {
    if (this.regoId || this.currentMapping) {
      console.log(this.mappingList[this.editDevice]);
      if (this.mappingList[this.editDevice]) {
        console.log('DUPLICATES! FREEZE!');
        return;
      }
      const m = new AutomationMapping();
      if (this.regoId) {
        m.regoId = this.regoId;
      } else {
        m.regoId = this.currentMapping.id;
      }
      m.automationId = this.editDevice;
      this.mappingList[this.editDevice] = m;
      await this.automationService.create(m);
      this.currentMapping = null;
      this.editDevice = '';
      this.changeDetectorRef.markForCheck();
      this.loadDevices();
    }
  }

  public getMapObjectTitle(obj: MapObject): string {
    return obj.title;
  }

  public getTitle(id: string): string {
    const r = this.mapObjectList.find(o => {
      return o.id + '' === id + '';
    });
    return r ? r.title : id;
  }

  public automap() {
    if (!this.selectedLayer) {
      return;
    }
    const features = this.mapService.getLayerFeatures().get(this.selectedLayer);
    this.mapObjectList = uniqBy<MapObject>(features.filter(o => {
      return o.layer.metadata;
    }).map(feature => convertFeatureToMapObject(feature)), 'id');
    for (let i = 0; i < this.deviceList.length; i++) {
      if (this.mappingList[this.deviceList[i]]) {
        continue;
      }
      const name = this.checkPostfix(this.checkPrefix(this.deviceList[i]));
      console.log('MAP', name);
      let regoObject = this.mapObjectList.find(o => {
        console.log(o);
        return o.title === name || o.id === name;
      });
      if (regoObject) {
        const m = new AutomationMapping();
        m.regoId = regoObject.id;
        m.automationId = this.deviceList[i];
        this.mappingList[this.deviceList[i]] = m;
        this.automationService.create(m);
      } else {
        regoObject = this.mapObjectList.find(o => {
          return o.title.includes(name) || o.id.includes(name);
        });
        if (regoObject) {
          const m = new AutomationMapping();
          m.regoId = regoObject.id;
          m.automationId = this.deviceList[i];
          this.mappingList[this.deviceList[i]] = m;
          this.automationService.create(m);
        }
      }
    }
    console.log(this.mappingList);
  }

  private checkPrefix(name: string): string {
    if (this.prefix.startsWith('!')) {
      if (name.startsWith(this.prefix.substring(1))) {
        return name.substring(this.prefix.length - 1);
      } else {
        return name;
      }
    } else {
      return this.prefix + name;
    }
  }

  private checkPostfix(name: string): string {
    if (this.postfix.startsWith('!')) {
      if (name.endsWith(this.postfix.substring(1))) {
        return name.substring(0, name.length - (this.postfix.length - 1));
      } else {
        return name;
      }
    } else {
      return name + this.postfix;
    }
  }

  public async UnMapDevice() {
    const mapping = this.mappingList[this.editDevice];
    await this.automationService.remove(mapping);
    this.editDevice = '';
    this.loadDevices();
  }
}
